/**
 * Branch Context for Admin PWA
 * Manages multi-location state
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BranchContext = createContext(null);

export function BranchProvider({ children }) {
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/branches`, {
        headers: {
          ...accountHeaders(),
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setBranches(data.branches || []);
        
        // Set current branch from localStorage or first branch
        const savedBranchId = localStorage.getItem('current_branch_id');
        const savedBranch = data.branches?.find(b => b.id === savedBranchId);
        setCurrentBranch(savedBranch || data.branches?.[0] || null);
      }
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    } finally {
      setLoading(false);
    }
  };

  const switchBranch = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    if (branch) {
      setCurrentBranch(branch);
      localStorage.setItem('current_branch_id', branchId);
    }
  };

  // Helper to add branch header to API calls
  const getBranchHeaders = useCallback(() => {
    return currentBranch ? { 'X-Branch-Id': currentBranch.id } : {};
  }, [currentBranch]);

  return (
    <BranchContext.Provider value={{ 
      branches, 
      currentBranch, 
      loading, 
      switchBranch,
      getBranchHeaders,
      refreshBranches: fetchBranches
    }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within BranchProvider');
  }
  return context;
}
